import { MapOverviewPoint } from "@/entities/map/model/map.types"
import { useEffect, useRef } from "react"

interface Props {
  height: number
  points: [MapOverviewPoint, MapOverviewPoint, MapOverviewPoint, MapOverviewPoint]
  src: string
  width: number
}

const GRID_SIZE = 10

function interpolate(
  points: [MapOverviewPoint, MapOverviewPoint, MapOverviewPoint, MapOverviewPoint],
  x: number,
  y: number
) {
  const [p1, p2, p3, p4] = points
  return {
    x: (1 - x) * (1 - y) * p1.x + x * (1 - y) * p2.x + x * y * p3.x + (1 - x) * y * p4.x,
    y: (1 - x) * (1 - y) * p1.y + x * (1 - y) * p2.y + x * y * p3.y + (1 - x) * y * p4.y
  }
}

function drawTriangle(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  source: [MapOverviewPoint, MapOverviewPoint, MapOverviewPoint],
  destination: [MapOverviewPoint, MapOverviewPoint, MapOverviewPoint]
) {
  const [s0, s1, s2] = source
  const [d0, d1, d2] = destination
  const denominator = s0.x * (s1.y - s2.y) + s1.x * (s2.y - s0.y) + s2.x * (s0.y - s1.y)
  if (!denominator) return

  const a = (d0.x * (s1.y - s2.y) + d1.x * (s2.y - s0.y) + d2.x * (s0.y - s1.y)) / denominator
  const b = (d0.y * (s1.y - s2.y) + d1.y * (s2.y - s0.y) + d2.y * (s0.y - s1.y)) / denominator
  const c = (d0.x * (s2.x - s1.x) + d1.x * (s0.x - s2.x) + d2.x * (s1.x - s0.x)) / denominator
  const d = (d0.y * (s2.x - s1.x) + d1.y * (s0.x - s2.x) + d2.y * (s1.x - s0.x)) / denominator
  const e =
    (d0.x * (s1.x * s2.y - s2.x * s1.y) + d1.x * (s2.x * s0.y - s0.x * s2.y) + d2.x * (s0.x * s1.y - s1.x * s0.y)) /
    denominator
  const f =
    (d0.y * (s1.x * s2.y - s2.x * s1.y) + d1.y * (s2.x * s0.y - s0.x * s2.y) + d2.y * (s0.x * s1.y - s1.x * s0.y)) /
    denominator

  context.save()
  context.beginPath()
  context.moveTo(d0.x, d0.y)
  context.lineTo(d1.x, d1.y)
  context.lineTo(d2.x, d2.y)
  context.closePath()
  context.clip()
  context.transform(a, b, c, d, e, f)
  context.drawImage(image, 0, 0)
  context.restore()
}

export default function OverviewWarpedMapImage({ height, points, src, width }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !width || !height) return
    const context = canvas.getContext("2d")
    if (!context) return

    const image = new Image()
    image.onload = () => {
      canvas.width = width
      canvas.height = height
      context.clearRect(0, 0, width, height)
      context.globalAlpha = 0.72

      for (let row = 0; row < GRID_SIZE; row++) {
        for (let column = 0; column < GRID_SIZE; column++) {
          const x0 = column / GRID_SIZE
          const x1 = (column + 1) / GRID_SIZE
          const y0 = row / GRID_SIZE
          const y1 = (row + 1) / GRID_SIZE
          const sourceTopLeft = { x: x0 * image.width, y: y0 * image.height }
          const sourceTopRight = { x: x1 * image.width, y: y0 * image.height }
          const sourceBottomRight = { x: x1 * image.width, y: y1 * image.height }
          const sourceBottomLeft = { x: x0 * image.width, y: y1 * image.height }
          const destinationTopLeft = interpolate(points, x0, y0)
          const destinationTopRight = interpolate(points, x1, y0)
          const destinationBottomRight = interpolate(points, x1, y1)
          const destinationBottomLeft = interpolate(points, x0, y1)

          drawTriangle(
            context,
            image,
            [sourceTopLeft, sourceTopRight, sourceBottomRight],
            [destinationTopLeft, destinationTopRight, destinationBottomRight]
          )
          drawTriangle(
            context,
            image,
            [sourceTopLeft, sourceBottomRight, sourceBottomLeft],
            [destinationTopLeft, destinationBottomRight, destinationBottomLeft]
          )
        }
      }
    }
    image.src = src

    return () => {
      image.onload = null
    }
  }, [height, points, src, width])

  return <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
}
